import { aiMemoryManager } from './ai-memory-manager'

interface UserProfile {
  userId: string
  preferences: Record<string, number>
  browsingHistory: string[]
  purchaseHistory: string[]
  favoriteCategories: string[]
  ratingHistory: Record<string, number>
}

interface ServiceItem {
  id: string
  name: string
  category: string
  price: number
  rating: number
  tags: string[]
  description: string
  popularity: number
  providerId: string
}

interface RecommendationResult {
  items: ServiceItem[]
  strategy: string
  confidence: number
  explanation: string
}

interface CollaborativeFilteringResult {
  userId: string
  similarUsers: string[]
  recommendedItems: string[]
}

export class AIRecommendationEngine {
  private userProfiles: Map<string, UserProfile> = new Map()
  private services: Map<string, ServiceItem> = new Map()
  private recommendationHistory: Map<string, string[]> = new Map()
  private isTraining = false

  constructor() {
    this.initializeMockData()
    this.startPeriodicTraining()
  }

  private initializeMockData() {
    // 不再初始化模拟数据，保持空数据状态，由真实数据填充
  }

  private startPeriodicTraining() {
    setInterval(() => {
      if (!this.isTraining) {
        this.trainModel()
      }
    }, 600000)
  }

  private async trainModel() {
    this.isTraining = true
    try {
      await this.updateUserPreferences()
      await this.updateServicePopularity()
    } finally {
      this.isTraining = false
    }
  }

  private async updateUserPreferences() {
    this.userProfiles.forEach((profile, userId) => {
      profile.browsingHistory.forEach(serviceId => {
        const service = this.services.get(serviceId)
        if (service) {
          profile.preferences[service.category] = (profile.preferences[service.category] || 0) + 0.1
        }
      })

      profile.purchaseHistory.forEach(serviceId => {
        const service = this.services.get(serviceId)
        if (service) {
          profile.preferences[service.category] = (profile.preferences[service.category] || 0) + 0.3
        }
      })
    })
  }

  private async updateServicePopularity() {
    // V1.3.63 无真实热度更新事件来源，不再对 popularity 做随机漂移；
    // 热度只允许由真实用户行为（浏览/购买，见 updateUserBehavior）驱动，此处保持原值
  }

  async getRecommendations(userId: string, limit: number = 10): Promise<RecommendationResult> {
    const profile = this.userProfiles.get(userId)
    const allServices = Array.from(this.services.values())

    if (!profile) {
      return this.getPopularRecommendations(limit)
    }

    const strategies = ['contentBased', 'collaborative', 'hybrid']
    // V1.3.63 策略选择改为确定性规则，不再随机：
    // 有购买记录（协同过滤有信号）→ hybrid；仅有浏览/偏好 → contentBased；无行为 → 取首个策略
    let selectedStrategy: string
    if (profile.purchaseHistory.length > 0) {
      selectedStrategy = 'hybrid'
    } else if (profile.browsingHistory.length > 0 || profile.favoriteCategories.length > 0 || Object.keys(profile.preferences).length > 0) {
      selectedStrategy = 'contentBased'
    } else {
      selectedStrategy = strategies[0]
    }

    let recommendedItems: ServiceItem[]
    let confidence: number
    let explanation: string

    switch (selectedStrategy) {
      case 'contentBased':
        recommendedItems = this.contentBasedFiltering(profile, allServices, limit)
        // V1.3.63 无真实命中率反馈前，使用确定性基线置信度，不加随机项
        confidence = 0.75
        explanation = '基于您的浏览和购买历史推荐'
        break
      case 'collaborative':
        recommendedItems = this.collaborativeFiltering(profile, allServices, limit)
        confidence = 0.70
        explanation = '基于相似用户的偏好推荐'
        break
      default:
        recommendedItems = this.hybridFiltering(profile, allServices, limit)
        confidence = 0.80
        explanation = '综合多种算法为您推荐'
        break
    }

    const filtered = this.deduplicateRecommendations(recommendedItems, userId)
    
    this.recommendationHistory.set(userId, filtered.map(i => i.id))

    return {
      items: filtered.slice(0, limit),
      strategy: selectedStrategy,
      confidence: Math.round(confidence * 100) / 100,
      explanation
    }
  }

  private contentBasedFiltering(profile: UserProfile, services: ServiceItem[], limit: number): ServiceItem[] {
    return services
      .filter(service => !profile.purchaseHistory.includes(service.id))
      .map(service => {
        let score = 0
        
        if (profile.favoriteCategories.includes(service.category)) {
          score += 0.4
        }
        
        score += (profile.preferences[service.category] || 0) * 0.3
        
        service.tags.forEach(tag => {
          const tagScore = Object.entries(profile.preferences).reduce((sum, [cat, pref]) => {
            return sum + (service.category === cat ? pref * 0.1 : 0)
          }, 0)
          score += tagScore
        })
        
        score += service.rating * 0.1
        score += service.popularity / 100 * 0.1
        
        return { service, score }
      })
      .sort((a, b) => b.score - a.score)
      .map(item => item.service)
      .slice(0, limit)
  }

  private collaborativeFiltering(profile: UserProfile, services: ServiceItem[], limit: number): ServiceItem[] {
    const similarUsers = this.findSimilarUsers(profile)
    
    const recommendations: Record<string, number> = {}
    
    similarUsers.forEach(similarUser => {
      similarUser.purchaseHistory.forEach(serviceId => {
        if (!profile.purchaseHistory.includes(serviceId)) {
          recommendations[serviceId] = (recommendations[serviceId] || 0) + 1
        }
      })
    })

    return services
      .filter(service => recommendations[service.id])
      .sort((a, b) => (recommendations[b.id] || 0) - (recommendations[a.id] || 0))
      .slice(0, limit)
  }

  private findSimilarUsers(profile: UserProfile): UserProfile[] {
    return Array.from(this.userProfiles.values())
      .filter(p => p.userId !== profile.userId)
      .map(other => {
        let similarity = 0
        
        Object.keys(profile.preferences).forEach(category => {
          const pref1 = profile.preferences[category] || 0
          const pref2 = other.preferences[category] || 0
          similarity += Math.abs(pref1 - pref2)
        })
        
        return { user: other, distance: similarity }
      })
      .sort((a, b) => a.distance - b.distance)
      .map(item => item.user)
      .slice(0, 3)
  }

  private hybridFiltering(profile: UserProfile, services: ServiceItem[], limit: number): ServiceItem[] {
    const contentBased = this.contentBasedFiltering(profile, services, limit * 2)
    const collaborative = this.collaborativeFiltering(profile, services, limit * 2)

    const scores: Record<string, number> = {}

    contentBased.forEach((service, index) => {
      scores[service.id] = (scores[service.id] || 0) + (limit * 2 - index) * 0.6
    })

    collaborative.forEach((service, index) => {
      scores[service.id] = (scores[service.id] || 0) + (limit * 2 - index) * 0.4
    })

    return services
      .filter(service => scores[service.id])
      .sort((a, b) => (scores[b.id] || 0) - (scores[a.id] || 0))
      .slice(0, limit)
  }

  private deduplicateRecommendations(items: ServiceItem[], userId: string): ServiceItem[] {
    const history = this.recommendationHistory.get(userId) || []
    const seen = new Set<string>()
    
    return items.filter(item => {
      if (seen.has(item.id) || history.includes(item.id)) {
        return false
      }
      seen.add(item.id)
      return true
    })
  }

  private getPopularRecommendations(limit: number): RecommendationResult {
    const items = Array.from(this.services.values())
      .sort((a, b) => b.popularity - a.popularity)
      .slice(0, limit)

    return {
      items,
      strategy: 'popular',
      confidence: 0.65,
      explanation: '为您推荐热门服务'
    }
  }

  async getCategoryRecommendations(category: string, limit: number = 6): Promise<RecommendationResult> {
    const items = Array.from(this.services.values())
      .filter(service => service.category === category)
      .sort((a, b) => b.rating * 0.6 + b.popularity * 0.4 - (a.rating * 0.6 + a.popularity * 0.4))
      .slice(0, limit)

    return {
      items,
      strategy: 'category',
      confidence: 0.85,
      explanation: `为您推荐${category}类别下的优质服务`
    }
  }

  async getProviderRecommendations(providerId: string, limit: number = 5): Promise<RecommendationResult> {
    const items = Array.from(this.services.values())
      .filter(service => service.providerId === providerId)
      .sort((a, b) => b.rating - a.rating)
      .slice(0, limit)

    return {
      items,
      strategy: 'provider',
      confidence: 0.8,
      explanation: '为您推荐该服务提供者的优质服务'
    }
  }

  async getSimilarServices(serviceId: string, limit: number = 5): Promise<RecommendationResult> {
    const service = this.services.get(serviceId)
    if (!service) {
      return this.getPopularRecommendations(limit)
    }

    const items = Array.from(this.services.values())
      .filter(s => s.id !== serviceId)
      .map(s => {
        let score = 0
        
        if (s.category === service.category) {
          score += 0.4
        }
        
        service.tags.forEach(tag => {
          if (s.tags.includes(tag)) {
            score += 0.15
          }
        })
        
        score += Math.max(0, 1 - Math.abs(s.price - service.price) / service.price) * 0.25
        
        score += s.rating * 0.2
        
        return { service: s, score }
      })
      .sort((a, b) => b.score - a.score)
      .map(item => item.service)
      .slice(0, limit)

    return {
      items,
      strategy: 'similar',
      confidence: 0.78,
      explanation: `与"${service.name}"类似的服务`
    }
  }

  updateUserBehavior(userId: string, action: 'view' | 'purchase' | 'rate', serviceId: string, rating?: number) {
    let profile = this.userProfiles.get(userId)
    
    if (!profile) {
      profile = {
        userId,
        preferences: {},
        browsingHistory: [],
        purchaseHistory: [],
        favoriteCategories: [],
        ratingHistory: {}
      }
      this.userProfiles.set(userId, profile)
    }

    switch (action) {
      case 'view':
        profile.browsingHistory.unshift(serviceId)
        profile.browsingHistory = profile.browsingHistory.slice(0, 50)
        break
      case 'purchase':
        profile.purchaseHistory.unshift(serviceId)
        profile.purchaseHistory = profile.purchaseHistory.slice(0, 50)
        break
      case 'rate':
        if (rating) {
          profile.ratingHistory[serviceId] = rating
        }
        break
    }

    const service = this.services.get(serviceId)
    if (service && !profile.favoriteCategories.includes(service.category)) {
      if (profile.purchaseHistory.filter(id => this.services.get(id)?.category === service.category).length >= 2) {
        profile.favoriteCategories.push(service.category)
      }
    }

    aiMemoryManager.addMemory(`用户${userId}${action === 'view' ? '浏览' : action === 'purchase' ? '购买' : '评分'}了服务${serviceId}`, 'fact', { importance: 2 })
  }

  addService(service: Omit<ServiceItem, 'id'>): ServiceItem {
    const newService: ServiceItem = {
      ...service,
      id: `service_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      popularity: 50
    }
    this.services.set(newService.id, newService)
    return newService
  }

  getService(serviceId: string): ServiceItem | undefined {
    return this.services.get(serviceId)
  }

  getAllServices(): ServiceItem[] {
    return Array.from(this.services.values())
  }

  getCategories(): string[] {
    return [...new Set(Array.from(this.services.values()).map(s => s.category))]
  }

  getStats() {
    return {
      totalUsers: this.userProfiles.size,
      totalServices: this.services.size,
      totalRecommendations: Array.from(this.recommendationHistory.values()).reduce((sum, arr) => sum + arr.length, 0),
      categories: this.getCategories().length,
      isTraining: this.isTraining
    }
  }
}

export const aiRecommendationEngine = new AIRecommendationEngine()